import { useState, type ReactElement } from "react";
import type { WidgetKind } from "@playbookai/types";
import { WidgetCard } from "./WidgetCard.js";
import { getWidget } from "./registry.js";
import type { WidgetItemAction } from "./types.js";

export interface DashboardWidgetProps<TView> {
  readonly kind: WidgetKind;
  /** Frozen widget data — typically from a TanStack Query result the caller provides. */
  readonly data: TView;
  /** Optional change handler — typically a TanStack mutation. If omitted, the widget is read-only. */
  readonly onChange?: (next: TView) => Promise<void> | void;
  /** Header context line (uppercase eyebrow above the title). */
  readonly context?: string;
  /** Short explanation shown directly below the title. */
  readonly description?: string;
  /** Optional title override; otherwise the widget def's `label` is used. */
  readonly title?: string;
  readonly className?: string;
  readonly bodyClassName?: string;
  /**
   * Optional per-item command forwarded to the widget body. The HOST owns what it
   * does; this adapter only passes it through, so `DashboardWidget` stays as
   * data-blind as the Component it renders.
   */
  readonly itemAction?: WidgetItemAction;
}

/**
 * Dashboard-mode widget adapter. Renders the same inner Component as
 * `<CanvasWidget>`, but framed in a `<WidgetCard>` (no drag handle, no
 * canvas positioning). The Component is data-blind — it receives
 * `{data, onChange, readOnly}` exactly as it does on the canvas.
 *
 * Decoupled from any specific data layer: the caller passes `data` (and
 * optionally `onChange`) directly. That means the caller can wrap with
 * TanStack Query, plain `fetch`, mock data — anything that yields a value.
 */
export function DashboardWidget<TView>({
  kind,
  data,
  onChange,
  context,
  description,
  title,
  className,
  bodyClassName,
  itemAction,
}: DashboardWidgetProps<TView>): ReactElement | null {
  const def = getWidget(kind);

  // Optimistic state — gives the inner Component a stable controlled value even
  // when the caller's onChange is async. Re-sync to the `data` prop when it
  // changes (server reconciliation, or the host re-resolving a widget's view-
  // model) so a prop update is never masked by a stale optimistic value — via
  // React's adjust-state-during-render pattern (no setState-in-effect). (Hooks
  // stay above the early return so they run unconditionally — rules-of-hooks.)
  const [local, setLocal] = useState<TView>(data);
  const [prevData, setPrevData] = useState<TView>(data);
  if (!Object.is(data, prevData)) {
    setPrevData(data);
    setLocal(data);
  }

  if (!def || !def.modes.includes("dashboard")) {
    // Always warn: this is a host-wiring mistake, and the library is
    // data-blind — no process.env (packages/ui CLAUDE.md §1).
    console.warn(
      `@playbookai/ui: widget '${kind}' does not support dashboard mode`,
    );
    return null;
  }

  const handleChange = (next: TView): void => {
    setLocal(next);
    if (onChange) {
      Promise.resolve(onChange(next)).catch((err: unknown) => {
        console.warn(
          `@playbookai/ui: dashboard widget '${kind}' onChange threw`,
          err,
        );
      });
    }
  };

  const Component = def.Component as React.ComponentType<{
    data: TView;
    onChange: (next: TView) => void;
    readOnly?: boolean | undefined;
    itemAction?: WidgetItemAction | undefined;
  }>;

  return (
    <WidgetCard
      title={title ?? def.label}
      {...(context !== undefined ? { context } : {})}
      {...(description !== undefined ? { description } : {})}
      {...(description !== undefined && def.dashboard?.icon
        ? { icon: def.dashboard.icon }
        : {})}
      {...(className !== undefined ? { className } : {})}
      {...(bodyClassName !== undefined ? { bodyClassName } : {})}
    >
      <Component
        data={local}
        onChange={handleChange}
        readOnly={!onChange}
        {...(itemAction !== undefined ? { itemAction } : {})}
      />
    </WidgetCard>
  );
}
